import { Text, View, Pressable, RefreshControl } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { deletePersonCustomFieldSection, savePersonCustomFieldSection } from '@/data/people';
import { deleteEventCustomFieldSection, saveEventCustomFieldSection } from '@/data/events';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { LegendList } from "@legendapp/list";
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import SearchBox from '@/components/General/inputs/SearchBox';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { showAlert } from '@/utils/showAlert';
import { useRouter, useNavigation, useLocalSearchParams } from 'expo-router';
import SortableList from '@/components/General/SortableList';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';
import PlanRestrictedScreen from '@/components/General/PlanRestrictedScreen';

// ============ SECTION CARD ============
const SectionCard = ({ item, type, router, onDelete }) => {
    const fieldCount = item.CustomFields?.length || 0;

    const menuOptions = useMemo(() => [
        {
            name: "Edit Section",
            icon: dropdownIcons.edit,
            onSelect: () => router.push({ pathname: "/customfields/sections/create", params: { id: item.id, type } }),
        },
        {
            name: "Delete Section",
            icon: dropdownIcons.delete,
            destructive: true,
            onSelect: () => showAlert({
                title: "Delete Section?",
                subtitle: `Are you sure you want to delete "${item.name}"? Fields in this section will become uncategorized.`,
                isDestructive: true,
                confirmText: "Delete",
                onConfirm: () => onDelete(item.id),
            }),
        },
    ], [item.id, item.name, type, router, onDelete]);

    const positionValue = item.position ?? 1000;
    const hasPosition = positionValue < 1000;

    return (
        <View testID={"SectionCard-" + item.name} style={styles.card}>
            <View style={styles.cardBody}>
                <Pressable
                    style={styles.cardPressable}
                    onPress={() => router.push({ pathname: "/customfields/sections/create", params: { id: item.id, type } })}
                >
                    <View style={styles.cardIconContainer}>
                        <Unicon name="layers-outline" size={20} colorVariant="primary" />
                    </View>
                    <View style={styles.cardContent}>
                        <Text numberOfLines={1} style={styles.cardTitle}>{item.name}</Text>
                        <View style={styles.cardMetaRow}>
                            <Unicon name="list-outline" size={12} colorVariant="textSecondary" />
                            <Text style={styles.fieldCountText}>{fieldCount} field{fieldCount !== 1 ? 's' : ''}</Text>
                        </View>
                        <View style={styles.cardMetaRow}>
                            <Unicon name={hasPosition ? "swap-vertical" : "alert-circle"} size={12} colorVariant={hasPosition ? "textSecondary" : "warning"} />
                            <Text style={hasPosition ? styles.positionText : styles.noPositionText}>
                                {hasPosition ? `Position ${positionValue + 1}` : "No Position Specified"}
                            </Text>
                        </View>
                    </View>
                </Pressable>
                <DropdownMenu testID="SectionCardMenu" options={menuOptions} />
            </View>
        </View>
    );
};

// ============ MAIN SCREEN ============
export default function CustomFieldSectionsScreen() {
    const { entitlements } = useOrganizationEntitlements();
    if (isFreeOrg(entitlements)) return <PlanRestrictedScreen />;
    return <CustomFieldSectionsScreenInner />;
}

function CustomFieldSectionsScreenInner() {
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [isDragModalOpen, setIsDragModalOpen] = useState(false);

    const queryClient = useQueryClient();
    const navigation = useNavigation();
    const router = useRouter();
    const { type } = useLocalSearchParams();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: personCustomFieldSections, isLoading: personCustomFieldSectionsLoading } = Query.personCustomFieldSections.all({ options: { enabled: type === "People" } });
    const { data: eventCustomFieldSections, isLoading: eventCustomFieldSectionsLoading } = Query.eventCustomFieldSections.all({ options: { enabled: type === "Events" } });
    const isLoading = personCustomFieldSectionsLoading || eventCustomFieldSectionsLoading;

    if (!loggedInUser?.can?.("Edit Custom Fields")) {
        router.back();
    }

    const personCustomFieldSectionPositionMutation = useMutation({
        mutationFn: savePersonCustomFieldSection,
        onSuccess: () => refreshList(),
    });

    const eventCustomFieldSectionPositionMutation = useMutation({
        mutationFn: saveEventCustomFieldSection,
        onSuccess: () => refreshList(),
    });

    const deletePersonFieldSectionMutation = useMutation({
        mutationFn: deletePersonCustomFieldSection,
        onSuccess: () => refreshList(),
    });

    const deleteEventFieldSectionMutation = useMutation({
        mutationFn: deleteEventCustomFieldSection,
        onSuccess: () => refreshList(),
    });

    useEffect(() => {
        navigation.setOptions({
            headerRight: () => (
                <DropdownMenu testID="HeaderMenu" options={[
                    { name: "Create a New Section", icon: dropdownIcons.add, onSelect: () => router.push({ pathname: "/customfields/sections/create", params: { type } }) },
                    { name: "Reorder Sections", icon: { android: "swap-vertical", ios: "arrow.up.arrow.down", web: "swap-vertical" }, onSelect: () => setIsDragModalOpen(true) },
                ]} />
            ),
        });
    }, [navigation, type]);

    useEffect(() => {
        const listData = type === "People" ? [...(personCustomFieldSections || [])] : [...(eventCustomFieldSections || [])];
        if (searchText) {
            setData(listData.filter(f => f.name?.toLowerCase().includes(searchText.toLowerCase())));
        } else {
            setData(listData);
        }
    }, [eventCustomFieldSections, personCustomFieldSections, searchText, type]);

    const refreshList = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.eventCustomFieldSections.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.personCustomFieldSections.all() });
    }, [queryClient]);

    const handleDelete = useCallback((sectionID) => {
        if (type === "People") deletePersonFieldSectionMutation.mutate(sectionID);
        else if (type === "Events") deleteEventFieldSectionMutation.mutate(sectionID);
    }, [type, deletePersonFieldSectionMutation, deleteEventFieldSectionMutation]);

    const renderItem = useCallback(({ item }) => (
        <SectionCard item={item} type={type} router={router} onDelete={handleDelete} />
    ), [type, router, handleDelete]);

    const handleReorder = useCallback((reorderedData) => {
        const newPositions = reorderedData.map((f, i) => ({ ...f, position: i }));
        if (type === "People") {
            newPositions.forEach(s => {
                s.Fields = s.CustomFields;
                personCustomFieldSectionPositionMutation.mutate(s);
            });
        } else {
            newPositions.forEach(s => {
                s.Fields = s.CustomFields;
                eventCustomFieldSectionPositionMutation.mutate(s);
            });
        }
        setTimeout(() => refreshList(), 1000);
    }, [type, personCustomFieldSectionPositionMutation, eventCustomFieldSectionPositionMutation, refreshList]);

    return (
        <View style={styles.container}>
            <View style={styles.contentWrapper}>
                {/* Search */}
                <View style={styles.searchContainer}>
                    <SearchBox searchText={searchText} onChangeText={setSearchText} placeholder="Search sections..." />
                </View>

                {/* List */}
                <View style={styles.listContainer}>
                    <LegendList
                        recycleItems
                        keyExtractor={(item) => item.id}
                        showsVerticalScrollIndicator={false}
                        keyboardShouldPersistTaps="handled"
                        estimatedItemSize={80}
                        data={isLoading ? [] : data}
                        renderItem={renderItem}
                        onEndReachedThreshold={0.1}
                        contentContainerStyle={styles.listContent}
                        refreshControl={<RefreshControl refreshing={false} onRefresh={refreshList} />}
                        ListFooterComponent={
                            isLoading ? (
                                <View style={styles.loadingContainer}><ActivityIndicator size="large" /></View>
                            ) : data.length === 0 ? (
                                <EmptyListPlaceholder message={searchText ? "No sections match your search" : "No sections yet"} />
                            ) : (
                                <View style={styles.listFooterSpacer} />
                            )
                        }
                    />
                </View>
            </View>

            <SortableList
                isOpen={isDragModalOpen}
                close={() => setIsDragModalOpen(false)}
                onConfirm={handleReorder}
                items={type === "People" ? personCustomFieldSections : eventCustomFieldSections}
                title={type === "People" ? "People Sections" : "Event Sections"}
                itemContent={(item) => <Text style={styles.sortableItemText}>{item?.name}</Text>}
                itemHeight={70}
            />
        </View>
    );
}

const styles = StyleSheet.create((theme) => ({
    container: { flex: 1, alignItems: "center" },
    contentWrapper: { flex: 1, maxWidth: 700, width: "100%" },
    searchContainer: { paddingHorizontal: 16, paddingTop: 12, paddingBottom: 4 },
    listContainer: { flex: 1, paddingHorizontal: 16 },
    listContent: { paddingBottom: 30 },
    loadingContainer: { padding: 40, alignItems: "center", justifyContent: "center" },
    listFooterSpacer: { paddingBottom: 30 },

    // Card
    card: {
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        marginTop: 8,
        borderWidth: 1,
        borderColor: theme.colors.border,
        overflow: "hidden",
        _web: {
            transition: "all 0.15s ease-in-out",
            _hover: { backgroundColor: theme.colors.hover },
        },
    },
    cardBody: {
        flexDirection: "row",
        alignItems: "center",
        padding: 14,
        gap: 12,
    },
    cardPressable: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
    },
    cardIconContainer: {
        width: 40,
        height: 40,
        borderRadius: 10,
        backgroundColor: theme.colors.primaryContainer,
        alignItems: "center",
        justifyContent: "center",
    },
    cardContent: { flex: 1 },
    cardTitle: {
        fontSize: 15,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    cardMetaRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8,
        marginTop: 4,
    },
    fieldCountText: {
        fontSize: 12,
        color: theme.colors.textSecondary,
    },
    positionText: {
        fontSize: 12,
        color: theme.colors.textSecondary,
    },
    noPositionText: {
        fontSize: 12,
        color: theme.colors.warning,
        fontStyle: "italic",
    },

    // Sortable List
    sortableItemText: {
        color: theme.colors.text,
        fontSize: 15,
        paddingLeft: 10,
        fontFamily: theme.fonts.heading,
    },
}))
